// src/pages/LoginPage.jsx
import React, { useEffect, useState } from "react";
import { auth, db, serverTimestamp } from "../firebaseConfig";
import {
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile,
} from "firebase/auth";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";

const upsertUser = async (user) => {
  if (!user) return;
  const ref = doc(db, "users", user.uid);
  const snap = await getDoc(ref);
  const base = {
    uid: user.uid,
    email: user.email || null,
    displayName: user.displayName || null,
    photoURL: user.photoURL || null,
    emailVerified: !!user.emailVerified,
    providerIds: user.providerData.map((p) => p.providerId),
  };
  if (snap.exists()) {
    await updateDoc(ref, { ...base, lastLoginAt: serverTimestamp() });
  } else {
    await setDoc(ref, {
      ...base,
      createdAt: serverTimestamp(),
      lastLoginAt: serverTimestamp(),
      role: "user",
      status: "active",
    });
  }
};

const LoginPage = () => {
  const [user, setUser] = useState(null);
  const [mode, setMode] = useState("signin"); // 'signin' | 'signup'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) await upsertUser(currentUser);
    });
    return () => unsub();
  }, []);

  const handleGoogle = async () => {
    setError("");
    setBusy(true);
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
      // onAuthStateChanged will upsert
    } catch (err) {
      console.error(err);
      setError("Nie udało się zalogować przez Google.");
    } finally {
      setBusy(false);
    }
  };

  const handleEmailAuth = async (e) => {
    e?.preventDefault();
    setError("");
    if (!email.trim() || !password.trim()) {
      setError("Podaj email i hasło.");
      return;
    }
    setBusy(true);
    try {
      if (mode === "signup") {
        const { user: u } = await createUserWithEmailAndPassword(auth, email.trim(), password);
        // ensure displayName at least from email prefix (optional)
        if (!u.displayName) {
          const name = email.split("@")[0];
          await updateProfile(u, { displayName: name });
        }
        await upsertUser(auth.currentUser);
      } else {
        await signInWithEmailAndPassword(auth, email.trim(), password);
        // onAuthStateChanged will upsert
      }
    } catch (err) {
      console.error(err);
      const code = err?.code || "";
      if (code.includes("auth/invalid-email")) setError("Nieprawidłowy adres e-mail.");
      else if (code.includes("auth/user-not-found")) setError("Użytkownik nie istnieje.");
      else if (code.includes("auth/wrong-password")) setError("Błędne hasło.");
      else if (code.includes("auth/weak-password")) setError("Hasło jest zbyt słabe (min. 6 znaków).");
      else if (code.includes("auth/email-already-in-use")) setError("Email jest już używany.");
      else setError("Wystąpił błąd. Spróbuj ponownie.");
    } finally {
      setBusy(false);
    }
  };

  const handleReset = async () => {
    setError("");
    if (!email.trim()) {
      setError("Podaj email, aby zresetować hasło.");
      return;
    }
    setBusy(true);
    try {
      await sendPasswordResetEmail(auth, email.trim());
      alert("Wysłaliśmy link do resetu hasła.");
    } catch (err) {
      console.error(err);
      setError("Nie udało się wysłać resetu hasła.");
    } finally {
      setBusy(false);
    }
  };

  const handleSignOut = async () => {
    setBusy(true);
    setError("");
    try {
      await signOut(auth);
    } catch (err) {
      console.error(err);
      setError("Błąd wylogowania.");
    } finally {
      setBusy(false);
    }
  };

  // Minimal inline styles; swap to your CSS vars if you like
  const S = {
    page: { minHeight: "100vh", display: "grid", placeItems: "center", background: "#f5f7fb", padding: 16, fontFamily: "Inter, system-ui, sans-serif", color: "#222" },
    card: { width: "100%", maxWidth: 420, background: "#fff", borderRadius: 14, boxShadow: "0 6px 24px rgba(0,0,0,0.08)", padding: 24 },
    header: { marginBottom: 16 },
    title: { margin: 0, fontSize: 22, fontWeight: 700 },
    subtitle: { margin: "6px 0 0 0", color: "#666", fontSize: 14 },
    tabs: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 16, marginBottom: 12, background: "#f1f3f7", padding: 6, borderRadius: 10 },
    tabBtn: (active) => ({ border: "none", background: active ? "#ffffff" : "transparent", boxShadow: active ? "0 2px 8px rgba(0,0,0,0.06)" : "none", borderRadius: 8, padding: "10px 12px", fontWeight: 600, cursor: "pointer" }),
    form: { display: "grid", gap: 12, marginTop: 8 },
    label: { fontSize: 13, color: "#444", fontWeight: 600 },
    input: { width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #e2e6ef", outline: "none", fontSize: 14 },
    row: { display: "flex", justifyContent: "space-between", alignItems: "center" },
    linkBtn: { background: "none", border: "none", color: "#0b7dda", cursor: "pointer", padding: 0, fontSize: 13, textDecoration: "underline" },
    error: { background: "#ffe9e9", color: "#a40000", border: "1px solid #ffcccc", padding: "8px 10px", borderRadius: 8, fontSize: 13 },
    divider: { display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", gap: 10, margin: "14px 0", color: "#888", fontSize: 12 },
    hr: { height: 1, background: "#eceff4", border: "none" },
    primaryBtn: { width: "100%", padding: "12px 14px", borderRadius: 12, border: "none", background: "var(--color-primary, #00a082)", color: "#fff", fontWeight: 700, fontSize: 15, cursor: "pointer" },
    googleBtn: { width: "100%", padding: "11px 14px", borderRadius: 12, border: "1px solid #e2e6ef", background: "#fff", fontWeight: 600, fontSize: 14, cursor: "pointer" },
    userBox: { display: "grid", gap: 8 },
    small: { fontSize: 13, color: "#555" },
  };

  return (
    <div style={S.page}>
      <div style={S.card}>
        {user ? (
          <div style={S.userBox}>
            <div style={S.header}>
              <h2 style={S.title}>Witaj, {user.displayName || "Użytkowniku"} 👋</h2>
              <p style={S.subtitle}>{user.email}</p>
            </div>
            <button style={{ ...S.primaryBtn, background: "#ef4444" }} onClick={handleSignOut} disabled={busy}>
              {busy ? "Wylogowywanie..." : "Wyloguj się"}
            </button>
          </div>
        ) : (
          <>
            <div style={S.header}>
              <h1 style={S.title}>Zaloguj się</h1>
              <p style={S.subtitle}>Użyj konta Google lub e-maila i hasła.</p>
            </div>

            {error ? <div style={S.error}>{error}</div> : null}

            <div style={S.tabs}>
              <button type="button" style={S.tabBtn(mode === "signin")} onClick={() => setMode("signin")}>
                Logowanie
              </button>
              <button type="button" style={S.tabBtn(mode === "signup")} onClick={() => setMode("signup")}>
                Rejestracja
              </button>
            </div>

            <form style={S.form} onSubmit={handleEmailAuth}>
              <div>
                <label style={S.label}>Email</label>
                <input
                  style={S.input}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="np. jan.kowalski@example.com"
                  autoComplete="email"
                />
              </div>
              <div>
                <label style={S.label}>Hasło</label>
                <input
                  style={S.input}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={mode === "signup" ? "min. 6 znaków" : "Twoje hasło"}
                  autoComplete={mode === "signup" ? "new-password" : "current-password"}
                />
              </div>
              <div style={S.row}>
                <span style={S.small}>
                  {mode === "signup" ? "Tworząc konto, akceptujesz regulamin." : "Masz problem z logowaniem?"}
                </span>
                {mode === "signin" && (
                  <button type="button" style={S.linkBtn} onClick={handleReset} disabled={busy}>
                    Resetuj hasło
                  </button>
                )}
              </div>

              <button type="submit" style={S.primaryBtn} disabled={busy}>
                {busy ? (mode === "signup" ? "Tworzenie konta..." : "Logowanie...") : mode === "signup" ? "Utwórz konto" : "Zaloguj się"}
              </button>
            </form>

            <div style={S.divider}>
              <hr style={S.hr} />
              <span>lub</span>
              <hr style={S.hr} />
            </div>

            <button style={S.googleBtn} onClick={handleGoogle} disabled={busy}>
              {busy ? "Łączenie..." : "Zaloguj się przez Google"}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
